// Auto-fix för AI-genererade workflow-imports.
//
// AI:n missar regelbundet samma 5-6 saker (snap-to-grid, port-inferens,
// port-uniqueness, collapsed-cx/cy, cykliska parents, cross-sheet-
// adjacency). Tidigare avvisade import:en hela workflow:t vid första
// fel — användaren fick ingen användbar feedback. Den här pipen kör
// EFTER expand (compact → full) och FÖRE onImport-dispatch, gör tysta
// fixar där det är säkert + rapporterar listan av fixes till callern
// så de kan visas i toast.
//
// Pure, deterministic, idempotent. Gör inga mutations på input — alla
// transforms producerar nya objekt.

import type {
  WorkflowState, MultiSheetWorkflowState, WorkflowSheet, WorkflowBlock,
  Connector, BlockGroup, CrossSheetConnector, PortSide,
} from '@/types/workflow';
import { isMultiSheetState } from '@/types/workflow';
import { getPortPosition, getOutwardVector } from '@/lib/connectorUtils';
import { isPortPairAvailable } from '@/lib/connectorPortRules';

const GRID = 20;
const DEFAULT_FROM_PORT: PortSide = 'right';
const DEFAULT_TO_PORT: PortSide = 'left';
const DEFAULT_BLOCK_W = 200;
const DEFAULT_BLOCK_H = 80;

// Cardinals first så att inferPorts:s tie-break (strikt-mindre)
// föredrar dem över diagonaler när två port-par har samma avstånd.
const ALL_PORTS: PortSide[] = [
  'top', 'right', 'bottom', 'left',
  'top-right', 'bottom-right', 'bottom-left', 'top-left',
];

function snap(v: number): number {
  return Math.round(v / GRID) * GRID;
}

/**
 * Pick the port-pair with the shortest straight-line distance between
 * two blocks. Mirrors the old `getOptimalPorts`-helper — used here as
 * fallback when the AI emits a connector without `fromPort`/`toPort`.
 */
function inferPorts(from: WorkflowBlock, to: WorkflowBlock): { fp: PortSide; tp: PortSide } {
  let best = { fp: DEFAULT_FROM_PORT, tp: DEFAULT_TO_PORT, dist: Infinity };
  for (const fp of ALL_PORTS) {
    const fpos = getPortPosition(from, DEFAULT_BLOCK_W, DEFAULT_BLOCK_H, fp);
    const fOut = getOutwardVector(fp);
    for (const tp of ALL_PORTS) {
      const tpos = getPortPosition(to, DEFAULT_BLOCK_W, DEFAULT_BLOCK_H, tp);
      const tOut = getOutwardVector(tp);
      // Penalize ports whose outward vectors face the wrong way (would
      // require the bezier to wrap around the block). Same outward
      // direction = +0.5*dist, opposite = -0.2*dist.
      const dot = fOut.dx * tOut.dx + fOut.dy * tOut.dy;
      const dist = Math.hypot(tpos.x - fpos.x, tpos.y - fpos.y) * (1 + dot * 0.3);
      if (dist < best.dist) best = { fp, tp, dist };
    }
  }
  return { fp: best.fp, tp: best.tp };
}

interface SheetFixResult {
  sheet: WorkflowSheet;
  fixes: string[];
}

/**
 * Apply all sheet-scoped auto-fixes to a single WorkflowSheet shape.
 * Returns a new sheet + a list of human-readable fixes for toast/log.
 */
function fixSheet(
  sheet: WorkflowSheet | (Pick<WorkflowState, 'blocks' | 'connectors' | 'groups'> & { id?: string; name?: string }),
  sheetLabel: string,
): SheetFixResult {
  const fixes: string[] = [];
  const blockIndex = new Map<string, WorkflowBlock>();

  // 1) Grid-snap blocks. AI:n drabbas av off-by-one där block landar på
  //    x=23 istället för 20. Render fungerar ändå men drag blir hoppigt
  //    eftersom resolveBlockPosition rundar mot 20px-grid:en.
  let snappedBlocks = 0;
  const fixedBlocks: WorkflowBlock[] = sheet.blocks.map((b) => {
    const sx = snap(b.x);
    const sy = snap(b.y);
    if (sx !== b.x || sy !== b.y) snappedBlocks++;
    const out: WorkflowBlock = { ...b, x: sx, y: sy };
    blockIndex.set(out.id, out);
    return out;
  });
  if (snappedBlocks > 0) {
    fixes.push(`${sheetLabel}: snapped ${snappedBlocks} block${snappedBlocks === 1 ? '' : 's'} to 20px grid`);
  }

  // 1.5) Strippa AI-emit:ade waypoints. Manuell dubbelklick-add är ENDA
  //      tillåtna källan; auto-injection (AI-prompt-output, legacy
  //      cloud-state, copy/paste mellan workflows) ska aldrig hamna i
  //      state. Render ignorerar dem ändå men det här gör avsikten
  //      explicit + ger user feedback.
  let droppedWaypointPayloads = 0;
  const stripped: Connector[] = sheet.connectors.map((c) => {
    if (!c.waypoints || c.waypoints.length === 0) return c;
    droppedWaypointPayloads++;
    const { waypoints: _w, ...rest } = c;
    void _w;
    return rest as Connector;
  });
  if (droppedWaypointPayloads > 0) {
    fixes.push(
      `${sheetLabel}: dropped waypoint payload from ${droppedWaypointPayloads} connector${
        droppedWaypointPayloads === 1 ? '' : 's'
      } (manual-only policy)`,
    );
  }

  // 2) Fyll i saknade fp/tp via geometri. Render-default (right→left)
  //    ger fula loopar när block inte ligger horisontellt.
  let inferredPorts = 0;
  const portInferred: Connector[] = fixedBlocks.length > 0
    ? stripped.map((c) => {
        if (c.fromPort && c.toPort) return c;
        const from = blockIndex.get(c.fromId);
        const to = blockIndex.get(c.toId);
        if (!from || !to) return c; // dangling — caught later
        const inferred = inferPorts(from, to);
        const next: Connector = {
          ...c,
          fromPort: c.fromPort ?? inferred.fp,
          toPort: c.toPort ?? inferred.tp,
        };
        if (next.fromPort !== c.fromPort || next.toPort !== c.toPort) inferredPorts++;
        return next;
      })
    : stripped;
  if (inferredPorts > 0) {
    fixes.push(`${sheetLabel}: inferred ports for ${inferredPorts} connector${inferredPorts === 1 ? '' : 's'}`);
  }

  // 3) Drop dangling connectors där from/to-blocket inte finns (AI:n
  //    glömmer ibland att uppdatera id-refs när den genererar).
  let droppedDangling = 0;
  const nonDangling: Connector[] = portInferred.filter((c) => {
    const ok = blockIndex.has(c.fromId) && blockIndex.has(c.toId);
    if (!ok) droppedDangling++;
    return ok;
  });
  if (droppedDangling > 0) {
    fixes.push(`${sheetLabel}: dropped ${droppedDangling} dangling connector${droppedDangling === 1 ? '' : 's'}`);
  }

  // 4) Port-uniqueness: drop connectors som re-använder en (block, port)
  //    mellan samma blockpar. Vi behåller den FÖRSTA länken per port —
  //    det är typiskt den AI:n menade.
  let portCollisions = 0;
  const accepted: Connector[] = [];
  for (const c of nonDangling) {
    if (isPortPairAvailable(accepted, c.fromId, c.toId, c.fromPort, c.toPort)) {
      accepted.push(c);
    } else {
      portCollisions++;
    }
  }
  if (portCollisions > 0) {
    fixes.push(`${sheetLabel}: dropped ${portCollisions} port-colliding connector${portCollisions === 1 ? '' : 's'}`);
  }

  // 5) Cyklisk parent (g.parentGroupId === g.id) eller dangling-parent
  //    → klipp parent-länken.
  const groupIds = new Set(sheet.groups.map((g) => g.id));
  let cyclicParents = 0;
  const fixedGroups: BlockGroup[] = sheet.groups.map((g) => {
    if (g.parentGroupId == null) return g;
    if (g.parentGroupId === g.id || !groupIds.has(g.parentGroupId)) {
      cyclicParents++;
      const { parentGroupId: _, ...rest } = g;
      void _;
      return rest as BlockGroup;
    }
    return g;
  });
  if (cyclicParents > 0) {
    fixes.push(`${sheetLabel}: cleared ${cyclicParents} cyclic/dangling parent reference${cyclicParents === 1 ? '' : 's'}`);
  }

  // 6) Collapsed groups (col:true) som saknar cx/cy → bbox-center av
  //    member-blocks. Render-fallback finns men placerar kortet vid
  //    (0,0) vilket lätt överlappar andra block.
  let cxCyFilled = 0;
  const groupsWithCxCy: BlockGroup[] = fixedGroups.map((g) => {
    if (!g.collapsed) return g;
    if (g.collapsedX != null && g.collapsedY != null) return g;
    const members = g.blockIds
      .map((bid) => blockIndex.get(bid))
      .filter((b): b is WorkflowBlock => !!b);
    if (members.length === 0) return g;
    const cx = snap(members.reduce((s, b) => s + b.x, 0) / members.length);
    const cy = snap(members.reduce((s, b) => s + b.y, 0) / members.length);
    cxCyFilled++;
    return { ...g, collapsedX: cx, collapsedY: cy };
  });
  if (cxCyFilled > 0) {
    fixes.push(`${sheetLabel}: filled cx/cy for ${cxCyFilled} collapsed group${cxCyFilled === 1 ? '' : 's'}`);
  }

  return {
    sheet: {
      id: 'id' in sheet && sheet.id ? sheet.id : crypto.randomUUID(),
      name: 'name' in sheet && sheet.name ? sheet.name : '',
      blocks: fixedBlocks,
      connectors: accepted,
      groups: groupsWithCxCy,
    },
    fixes,
  };
}

export interface AutoFixResult<T> {
  state: T;
  fixes: string[];
}

export function autoFixSingleSheet(state: WorkflowState): AutoFixResult<WorkflowState> {
  const result = fixSheet(state, 'flöde');
  return {
    state: {
      ...state,
      blocks: result.sheet.blocks,
      connectors: result.sheet.connectors,
      groups: result.sheet.groups,
    },
    fixes: result.fixes,
  };
}

export function autoFixMultiSheet(
  state: MultiSheetWorkflowState,
): AutoFixResult<MultiSheetWorkflowState> {
  const allFixes: string[] = [];
  const fixedSheets = state.sheets.map((s, i) => {
    const label = s.name?.trim() ? `sheet "${s.name.trim()}"` : `sheet ${i + 1}`;
    const result = fixSheet(s, label);
    allFixes.push(...result.fixes);
    return result.sheet;
  });

  // Cross-sheet-connectors: drop |Δ|>1 + dangling endpoints.
  const sheetCount = fixedSheets.length;
  const blockToSheet = new Map<string, number>();
  fixedSheets.forEach((s, i) => {
    for (const b of s.blocks) blockToSheet.set(b.id, i);
  });
  let droppedNonAdjacent = 0;
  let droppedDanglingCSC = 0;
  const fixedCSCs: CrossSheetConnector[] = (state.crossSheetConnectors ?? []).filter((c) => {
    const inRange = c.fromSheetIndex >= 0 && c.fromSheetIndex < sheetCount &&
                    c.toSheetIndex >= 0 && c.toSheetIndex < sheetCount;
    // Adjacency constraint lifted in Ship 7 (Caveat 5A): allow any
    // two distinct sheets. Only reject out-of-range + self-loop.
    const valid = inRange && c.fromSheetIndex !== c.toSheetIndex;
    if (!valid) {
      droppedNonAdjacent++;
      return false;
    }
    const fromOk = blockToSheet.get(c.fromBlockId) === c.fromSheetIndex;
    const toOk = blockToSheet.get(c.toBlockId) === c.toSheetIndex;
    if (!fromOk || !toOk) {
      droppedDanglingCSC++;
      return false;
    }
    return true;
  });
  if (droppedNonAdjacent > 0) {
    allFixes.push(`dropped ${droppedNonAdjacent} non-adjacent cross-sheet connector${droppedNonAdjacent === 1 ? '' : 's'}`);
  }
  if (droppedDanglingCSC > 0) {
    allFixes.push(`dropped ${droppedDanglingCSC} dangling cross-sheet connector${droppedDanglingCSC === 1 ? '' : 's'}`);
  }

  return {
    state: {
      ...state,
      sheets: fixedSheets,
      crossSheetConnectors: fixedCSCs,
    },
    fixes: allFixes,
  };
}

/**
 * Top-level entry: dispatcha mellan single- och multi-sheet automatiskt.
 */
export function autoFixImport(
  state: WorkflowState | MultiSheetWorkflowState,
): AutoFixResult<WorkflowState | MultiSheetWorkflowState> {
  if (isMultiSheetState(state)) return autoFixMultiSheet(state);
  return autoFixSingleSheet(state);
}
